import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Call, Device } from '@twilio/voice-sdk';
import jwt_decode from 'jwt-decode';
import { Observable, of, Subject, Subscription, throwError, timer as rxTimer } from 'rxjs';
import { catchError, concatMap, last, map, retry, scan, switchMap, take, takeWhile, timeout } from 'rxjs/operators';
import { CallInProgressDialogComponent } from 'src/app/dialogs/calls/call-in-progress-dialog/call-in-progress-dialog.component';
import { IncomingCallDialogComponent } from 'src/app/dialogs/calls/incoming-call-dialog/incoming-call-dialog.component';
import { MicrophoneRequiredDialogComponent } from 'src/app/dialogs/calls/microphone-required-dialog/microphone-required-dialog.component';
import { ApiResponse } from 'src/app/models/api-response.model';
import {
  CallAudioWarning,
  callWarningDetector,
  CallWarningData,
  REPORTED_CALL_WARNINGS,
} from 'src/app/models/call-audio-warning.model';
import { CallClientContext, CallClientError } from 'src/app/models/call-client-error.model';
import {
  isMicrophoneProblem,
  isMicrophoneUnavailable,
  MICROPHONE_PROBLEM_HELP,
  MicrophoneProblem,
  MicrophoneRequiredDialogData,
  MicrophoneRequiredDialogResult,
} from 'src/app/models/microphone-availability.model';
import { isReportableMicrophoneFailure, MicrophoneCheckResult } from 'src/app/models/microphone-check.model';
import { Patient } from 'src/app/models/patient.model';
import { OutboundCallerIdDecision } from 'src/app/shared/models/caller-id-decision.model';
import { PhoneConfigEntry } from 'src/app/shared/models/phone-config.model';
import {
  POST_CALL_SURVEY_OUTCOME_CHECKS,
  POST_CALL_SURVEY_OUTCOME_FIRST_CHECK_MS,
  POST_CALL_SURVEY_OUTCOME_INTERVAL_MS,
  POST_CALL_SURVEY_OUTCOME_REQUEST_TIMEOUT_MS,
  POST_CALL_SURVEY_REGISTER_RETRIES,
  POST_CALL_SURVEY_REGISTER_RETRY_DELAY_MS,
  POST_CALL_SURVEY_SETTLED_STATUSES,
  PostCallSurveyOutcome,
} from 'src/app/models/post-call-survey.model';
import { CallReadiness } from 'src/app/models/twilio-device.model';
import { environment } from 'src/environments/environment';
import { FirestoreService } from '../firestore.service';
import { FirebaseIdTokenService } from '../auth/firebase-id-token.service';
import { TokenService } from '../auth/token.service';
import { ClientErrorReporter } from '../telemetry/client-error-reporter.service';
import { MicrophoneAvailabilityService } from './microphone-availability.service';
import { MicrophoneCheckService } from './microphone-check.service';
import { TwilioDeviceCoordinatorService } from './twilio-device-coordinator.service';
import { AddParticipantResponse } from './models/add-participant.response';
import { CallSession, DISCHARGE_CALL_TYPE, WARM_TRANSFER_CALL_TYPE } from './models/call.model';
import { Attachment, SendTextResponse } from './models/communication.model';
import { HoldParticipantResult } from './models/hold-participant.model';

/**
 * A connected call this short is the symptom being investigated: the care manager realises the
 * patient cannot hear them and hangs up. Kept slightly above the 12s used for the failure-rate
 * measurement so the reported population contains the whole of it.
 */
const SHORT_CALL_SECONDS = 15;

/** How long to wait for the SDK to re-read its device list after access is granted: 10 × 100 ms. */
const SDK_DEVICE_REFRESH_ATTEMPTS = 10;
const SDK_DEVICE_REFRESH_INTERVAL_MS = 100;

@Injectable({
  providedIn: 'root',
})
export class TwilioService {
  twilioDefaultLine: string;
  /**
   * Quality warnings raised by the SDK on the active call, for whichever call UI is on screen.
   *
   * Exposed as a stream rather than handled here because the service cannot know which dialog owns
   * the call; it reports every warning to the backend regardless of whether anyone is listening.
   */
  readonly audioWarnings$ = new Subject<CallAudioWarning>();
  /** Tail of the audio-device operation chain — see {@link runExclusively}. */
  private audioOperations: Promise<unknown> = Promise.resolve();
  /**
   * Reading taken by the last pre-call microphone check.
   *
   * Held because the check necessarily runs BEFORE the call exists, so it has no CallSid to be
   * joined on. It is re-reported once the call connects — see {@link monitorCallAudio}.
   */
  private lastMicrophoneReading: MicrophoneCheckResult | null = null;
  /** The one required-microphone dialog that may be open; a second problem must not stack another. */
  private microphoneDialogRef: MatDialogRef<MicrophoneRequiredDialogComponent, MicrophoneRequiredDialogResult> | null = null;
  private microphoneWatch: Subscription | null = null;
  /** The microphone became unavailable during a call; the alert waits for the call to end. */
  private microphoneAlertPending = false;
  private twilioIdentity: string;
  /** `iat`/`exp` of the token the Device holds, so an error report can say how stale it was. */
  private twilioTokenClaims: { iat?: number; exp?: number } | null = null;
  /** When the tab last changed between visible and hidden. Starts at load, which counts as a change. */
  private visibilityChangedAt = Date.now();
  private device: Device;
  /**
   * The fresh token fetched at sign-in, used if this tab gets the Device right away. A tab that gets
   * it later (another tab closed, or it took over) fetches its own, since this one may be stale.
   */
  private startupToken: string | null = null;
  /** In-flight Device creation, so a call waits for it instead of finding no Device. */
  private activation: Promise<void> | null = null;
  /** In-flight token refresh, shared by every caller that needs a fresh token at the same time. */
  private tokenRefresh: Promise<boolean> | null = null;
  private tokenRefreshRetry: ReturnType<typeof setTimeout> | null = null;
  private activationRetry: ReturnType<typeof setTimeout> | null = null;
  /** Refreshes the token ahead of its own `exp`, independently of when the SDK raises tokenWillExpire. */
  private scheduledTokenRefresh: ReturnType<typeof setTimeout> | null = null;
  /** The last token this tab held, reused when it takes the Device back while it is still fresh. */
  private lastToken: string | null = null;
  /**
   * Set between prepareForCall() and the moment the SDK reports the call. In that window (microphone
   * check, connect) `Device.isBusy` is still false, and another tab must not take the Device.
   */
  private callPending: ReturnType<typeof setTimeout> | null = null;
  private visibleTakeover: ReturnType<typeof setTimeout> | null = null;
  private welbyCoreApiBaseUrl = environment.welbyEndpoint;
  /**
   * Interceptor-free client for the calls that must never sign the care manager out: telemetry (see
   * reportClientCallError) and the Twilio token fetch (see fetchTokenWithoutSignOut).
   */
  private telemetryHttp: HttpClient;
  private welbyCommunicationEndpoint = environment.markusApiUrl;
  constructor(
    private http: HttpClient,
    private firestoreService: FirestoreService,
    private dialog: MatDialog,
    private tokenService: TokenService,
    private microphoneCheck: MicrophoneCheckService,
    private errorReporter: ClientErrorReporter,
    private microphoneAvailability: MicrophoneAvailabilityService,
    private coordinator: TwilioDeviceCoordinatorService,
    httpBackend: HttpBackend,
    private firebaseIdToken: FirebaseIdTokenService
  ) {
    this.telemetryHttp = new HttpClient(httpBackend);
    this.getDefaultTwilioLine();
    // Never removed: the service is a root singleton that lives as long as the tab.
    document.addEventListener('visibilitychange', () => {
      this.visibilityChangedAt = Date.now();
      if (document.visibilityState === 'visible') {
        this.onTabShown();
      } else {
        this.cancelVisibleTakeover();
      }
    });
    // A laptop waking up or the network coming back leaves the Device unregistered with a token that
    // may have expired meanwhile; both are repaired before the care manager tries to call.
    window.addEventListener('online', () => this.repairDevice());
    document.addEventListener('resume', () => this.repairDevice());
  }

  /** True when a call placed now from this tab would be answered by this tab's Device. */
  get isActiveCallingTab(): boolean {
    return !this.coordinator.isSupported || this.coordinator.isHolder;
  }

  getDefaultTwilioLine(): void {
    this.firestoreService.doc$('welby_configuration/twilio_lines').subscribe((line: { defaultLine: string }) => {
      this.twilioDefaultLine = line.defaultLine;
    });
  }

  /**
   * Called at sign-in. With several Welby tabs open only one of them holds the Device (see
   * {@link TwilioDeviceCoordinatorService}); this tab creates it when it becomes the holder.
   */
  async initializeTwilio(token: string, identity: string): Promise<void> {
    this.twilioIdentity = identity;
    this.startupToken = token;
    // In every tab, Device or not: the microphone alert must show where the care manager is looking.
    this.watchMicrophoneAvailability();
    if (!this.coordinator.isSupported) {
      // Browsers without Web Locks keep the previous behaviour: one Device per tab.
      await this.activateDevice();
      return;
    }
    this.coordinator.start(identity, {
      onAcquired: () => this.activateDevice(),
      onLost: () => this.releaseDevice(),
      isBusy: () => this.isBusy(),
      onHolderIdle: () => {
        if (document.visibilityState === 'visible') {
          this.onTabShown();
        }
      },
    });
  }

  /** Sign-out: hand the Device back and forget the identity. */
  disconnectDevice(): void {
    this.coordinator.stop();
    if (this.device) {
      this.device.disconnectAll();
    }
    this.releaseDevice();
    this.twilioIdentity = null;
    this.microphoneWatch?.unsubscribe();
    this.microphoneWatch = null;
    this.microphoneAlertPending = false;
    this.microphoneAvailability.stop();
    this.microphoneDialogRef?.close({ resolved: false });
    this.startupToken = null;
    this.lastToken = null;
    // Still in flight for the user who signed out: the next user must not reuse them. They check the
    // identity themselves when they resolve and drop their result.
    this.activation = null;
    this.tokenRefresh = null;
    this.clearCallPending();
    this.cancelVisibleTakeover();
  }

  /**
   * Opens the required-microphone alert, or returns the one already open.
   *
   * Resolves with `resolved: true` when it closed because the microphone came back, and `false` when
   * the care manager chose "Not now".
   */
  openMicrophoneRequiredDialog(problem: MicrophoneProblem): Promise<MicrophoneRequiredDialogResult | undefined> {
    if (!this.microphoneDialogRef) {
      const data: MicrophoneRequiredDialogData = { problem };
      this.microphoneDialogRef = this.dialog.open(MicrophoneRequiredDialogComponent, { width: '30rem', data });
      this.microphoneDialogRef.afterClosed().subscribe(() => (this.microphoneDialogRef = null));
    }
    return this.microphoneDialogRef.afterClosed().toPromise();
  }

  /**
   * Makes sure a call can be placed from this tab, before the microphone check and the dial.
   *
   * - Takes the Device over from another tab, unless that tab is on a call.
   * - Refreshes the token if it is close to expiring.
   * - Waits for the Device to be registered, re-registering it if it dropped (31009 → 20101 in
   *   background tabs left it `unregistered`). If it still is not registered after
   *   `registrationTimeoutMs` the call is tried anyway; only a missing Device is `unavailable`.
   */
  async prepareForCall(): Promise<CallReadiness> {
    if (!this.twilioIdentity) {
      return 'unavailable';
    }
    if (this.coordinator.isSupported && !(await this.coordinator.takeOver())) {
      return 'busy-in-another-tab';
    }
    // Claimed before anything else is awaited: from here until the SDK reports the call, another tab
    // must not take the Device away.
    this.markCallPending();
    if (!this.device) {
      await (this.activation ?? this.activateDevice());
    }
    if (await this.ensureRegistered()) {
      return 'ready';
    }
    // Registration only matters for incoming calls: connect() works on an unregistered Device while
    // signaling is up. So the call is still tried, and the registration failure is reported. Not on an
    // expired token, though: that dial would fail after the wait with no message in the dialog.
    const device = this.device;
    const exp = this.twilioTokenClaims?.exp;
    const tokenAlive = typeof exp === 'number' && exp > Date.now() / 1000;
    if (device && device.state !== Device.State.Destroyed && tokenAlive) {
      this.errorReporter.report('twilio-device', new Error(`Device state: ${device.state}`), {
        prefix: 'Twilio Device not registered in time, dialing anyway',
      });
      return 'ready';
    }
    this.clearCallPending();
    return 'unavailable';
  }

  /** The dialog decided not to dial after prepareForCall() (e.g. the microphone check failed). */
  cancelPreparedCall(): void {
    this.clearCallPending();
    this.afterCallEnded();
  }

  async getToken(identity = 'welbyHealth'): Promise<string> {
    const response = await this.http
      .get<{ token: string }>(`${this.welbyCoreApiBaseUrl}/api/v1/twilio/token`, { params: { identity } })
      .toPromise();
    return response.token;
  }

  /**
   * `voicemailMessageId` is decided before dialling and travels with the call so the backend can act
   * the instant the mailbox picks up. Answering machine detection only reports at the beep, when the
   * mailbox is already recording, so a choice made after that point is recorded as silence.
   */
  async makeBrowserCall(params: {
    To: string;
    from?: string;
    careManagerFullName?: string;
    patientId?: string;
    voicemailMessageId?: string;
  }): Promise<Call> {
    // patientId is context for failure reporting only — it must not be forwarded to Twilio as a
    // TwiML parameter, so it is stripped before connect().
    const { patientId } = params;
    const connectParams: Record<string, string> = { To: params.To };
    if (params.from) {
      connectParams.from = params.from;
    }
    if (params.careManagerFullName) {
      connectParams.careManagerFullName = params.careManagerFullName;
    }
    if (params.voicemailMessageId) {
      connectParams.voicemailMessageId = params.voicemailMessageId;
    }

    try {
      if (this.device) {
        const call = await this.device.connect({ params: connectParams });
        // The SDK now tracks the call, so `isBusy` takes over from the pending flag.
        this.clearCallPending();
        const ended = () => this.afterCallEnded();
        call.on('disconnect', ended);
        call.on('cancel', ended);
        call.on('reject', ended);

        call.on('error', (err) => {
          console.error('[Twilio] Call error', err);
          this.reportClientCallError({
            code: this.sdkErrorCode(err),
            message: err?.message ?? 'Unknown call error',
            source: 'call',
            callSid: call.parameters?.CallSid,
            patientId,
            fromLine: params.from,
          });
        });
        this.monitorCallAudio(call, { patientId, fromLine: params.from });
        return call;
      } else {
        this.cancelPreparedCall();
        console.error('Twilio device is not initialized');
        this.reportClientCallError({
          message: 'Twilio device is not initialized',
          source: 'connect',
          deviceNotReady: true,
          patientId,
          fromLine: params.from,
        });
        return null;
      }
    } catch (error) {
      this.cancelPreparedCall();
      console.error('Error making call: ', error);
      this.reportClientCallError({
        code: this.sdkErrorCode(error),
        message: (error as Error)?.message ?? 'Unknown error',
        source: 'connect',
        patientId,
        fromLine: params.from,
      });
      return null;
    }
  }

  /**
   * Acquires the microphone and proves it is capturing before a call is placed.
   *
   * Solves a specific failure: roughly one in five answered calls reached Twilio with a silent
   * browser channel while the patient channel was normal — measured on the dual-channel recordings,
   * which are made on Twilio's media server, so the audio never left the browser. The failures were
   * interleaved with healthy calls minutes apart on the same machine, which rules out the headset
   * and the network and points at the track handed to the call.
   *
   * Two things have to happen together, and neither works alone:
   *
   * 1. **Own the input device.** Left to itself the SDK calls `getUserMedia` per call and keeps the
   *    result in `_defaultInputDeviceStream`, but the moment a `devicechange` fires (a headset
   *    waking, a USB hub re-enumerating, the OS default switching) it promotes that to a *selected*
   *    device and caches the stream on the Device, reusing it for every later call. A cached track
   *    that has since been muted or ended by the OS then feeds silence into call after call with no
   *    error anywhere. Calling `setInputDevice` here, and {@link releaseMicrophone} when the call
   *    ends, forces a fresh acquisition for every call instead.
   * 2. **Verify the track that will actually be sent.** `device.audio.inputStream` is the very
   *    stream the Call will use, so it is the one measured — checking a separately acquired stream
   *    would prove nothing about it.
   *
   * A failed check is retried once with a forced re-acquisition, because the common case is a stale
   * device that recovers the moment it is re-opened.
   */
  ensureMicrophoneReady(context: { patientId?: string; fromLine?: string } = {}): Promise<MicrophoneCheckResult> {
    return this.runExclusively(() => this.acquireAndVerifyMicrophone(context));
  }

  /**
   * Releases the input device once a call is over.
   *
   * Two reasons, both load-bearing. The SDK keeps a device selected with `setInputDevice` capturing
   * until it is explicitly unset, so without this the browser would show the recording indicator
   * between calls; and, more importantly, releasing is what guarantees the next call re-acquires the
   * microphone instead of inheriting a track that may have died in the meantime.
   *
   * Safe to call unconditionally: the SDK rejects while a call is still up, and that rejection is
   * deliberately swallowed.
   */
  async releaseMicrophone(): Promise<void> {
    await this.runExclusively(() => this.device?.audio?.unsetInputDevice() ?? Promise.resolve()).catch(() => undefined);
  }

  /**
   * Reports a Twilio Voice SDK error to the backend so the attempt leaves a durable trace.
   *
   * Fire-and-forget by design: a telemetry failure must not alter the call flow nor surface anything
   * to the care manager. The envelope is unwrapped here in the service, never in a component.
   *
   * Deliberately sent through `HttpBackend`, which BYPASSES the global interceptor chain. That is not
   * a style choice: `AuthInterceptor` turns any 401 on an `/api/v1` URL into `signOut()` +
   * `location.reload()`, and it omits the Authorization header entirely once the token has expired —
   * so an expired token guarantees a 401. The errors most likely to trigger this report are Twilio's
   * own token errors (31204/31205), which occur in exactly that window. Left on the normal client,
   * reporting a token error would sign the care manager out in the middle of a call. The token is
   * attached by hand instead; if it is missing or expired the report is simply lost, which is the
   * correct trade for telemetry.
   */
  reportClientCallError(payload: CallClientError): void {
    const headers = this.tokenService.tokenIsExpired() ? {} : { Authorization: `Bearer ${this.tokenService.getToken()}` };

    this.telemetryHttp
      .post<ApiResponse<void>>(
        `${this.welbyCoreApiBaseUrl}/api/v1/call-attempts/client-error`,
        this.sanitizeClientError({ ...payload, clientContext: this.describeClientContext() }),
        { headers }
      )
      .pipe(
        map((res) => res.data),
        take(1)
      )
      .subscribe({
        error: (err) => console.error('[Twilio] Failed to report call error', err),
      });
  }

  /**
   * The first token fetch, at sign-in. If it fails there is no Device: calls cannot be placed or
   * received, and until now nothing said so anywhere.
   */
  reportTokenFetchFailure(error: unknown): void {
    console.error('[Twilio] Could not fetch the Voice token; calling is unavailable', error);
    this.errorReporter.report('twilio-device', error, { prefix: 'Twilio token fetch failed' });
  }

  addDeviceListeners(device: Device): void {
    device.on('registered', () => {
      console.log('Twilio.Device Ready to make and receive calls!');
    });

    device.on('incoming', (call) => {
      this.showIncomingCallDialog(call);
    });

    device.on('tokenWillExpire', () => {
      this.refreshToken();
    });

    device.on('error', (error) => {
      console.error('Twilio.Device Error: ' + error.message);
      this.reportClientCallError({
        code: this.sdkErrorCode(error),
        message: error?.message ?? 'Unknown device error',
        source: 'device',
      });
    });
  }

  /**
   * Sends an SMS text message to a patient via Twilio.
   *
   * @param receiver - The patient receiving the text message
   * @param sender - The patient/provider sending the text message
   * @param text - The message content to send
   * @param attachments - Optional array of file attachments to include with the message
   * @param numberFilesExceed2MB - Optional count of files exceeding 2MB size limit
   * @param smsContact - Optional override phone number for the receiver (defaults to receiver.sms_contact)
   * @returns A Promise that resolves to the SendTextResponse containing the message delivery status
   */
  sendPatientText(
    receiver: Patient,
    sender: Patient,
    text: string,
    attachments?: Attachment[],
    numberFilesExceed2MB?: number,
    smsContact?: string
  ): Promise<SendTextResponse> {
    const to = smsContact ?? receiver.sms_contact;
    const twilioLine = sender?.twilio_line ?? this.twilioDefaultLine;
    const body = {
      from: twilioLine,
      to,
      message: text,
      patientId: receiver.user_id,
      patientName: `${receiver.firstName} ${receiver.lastName}`,
      provider: {
        firstName: sender.firstName,
        lastName: sender.lastName,
        uid: sender.user_id,
        twilio_line: sender.twilio_line,
      },
      providerId: sender.user_id,
      senderId: sender.user_id,
      senderName: `${sender.firstName} ${sender.lastName}`,
      receiverId: receiver.user_id,
      receiverName: `${receiver.firstName} ${receiver.lastName}`,
      attachments,
      useAttachementsLinks: attachments?.length > 1 || numberFilesExceed2MB > 0,
    };
    return this.postTextMessage(body);
  }

  /**
   * Sends an SMS text message to an external phone number (non-patient) via Twilio.
   *
   * @param sender - The patient/provider sending the text message
   * @param receiver - The phone number to send the message to
   * @param text - The message content to send
   * @returns A Promise that resolves to the SendTextResponse containing the message delivery status
   */
  sendExternalText(sender: Patient, receiver: string, text: string): Promise<SendTextResponse> {
    const twilio_line = sender?.twilio_line ?? this.twilioDefaultLine;
    const body = {
      from: twilio_line,
      to: receiver,
      message: text,
      providerName: `${sender.firstName} ${sender.lastName}`,
      providerId: sender.user_id,
      senderId: sender.user_id,
      senderName: `${sender.firstName} ${sender.lastName}`,
    };
    return this.postTextMessage(body);
  }

  /**
   * Asks the backend which of the client's lines to call this patient from (NP-04 step 2).
   *
   * The client is resolved server-side from the patient, so it is deliberately not a parameter:
   * letting the caller name it would allow sourcing a caller ID from another clinic.
   *
   * A `phoneNumber: null` decision is a normal outcome meaning "the staff chooses", not a failure.
   */
  getOutboundCallerIdDecision(patientId: string): Observable<OutboundCallerIdDecision> {
    const url = `${this.welbyCoreApiBaseUrl}/api/v1/patients/${patientId}/outbound-caller-id`;
    return this.http.get<ApiResponse<OutboundCallerIdDecision>>(url).pipe(map((response) => response.data));
  }

  /**
   *   * Adds a participant to an ongoing call
   *
   * @param callId The ID of the call to add the participant to
   * @param phoneNumber The phone number to add to the call (in E.164 format, e.g., +15551234567)
   * @returns An Observable that completes when the request is done
   */
  addParticipantToCall(
    parentCallSid: string,
    phoneNumberTo: string,
    phoneNumberFrom?: string
  ): Observable<AddParticipantResponse> {
    const response = this.http.post<AddParticipantResponse>(
      `${this.welbyCoreApiBaseUrl}/api/v1/twilio/${parentCallSid}/add-participant`,
      {
        phoneFrom: phoneNumberFrom,
        phoneTo: phoneNumberTo,
      }
    );

    return response;
  }

  /**
   * Hangs up a specific participant in a call
   *
   * @param callSid The SID of the call
   * @param participantSid The SID of the participant to hang up
   * @returns Observable with the hang-up result
   */
  hangUpParticipant(callSid: string, participantSid: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.welbyCoreApiBaseUrl}/api/v1/twilio/${callSid}/participants/${participantSid}/hangup`,
      {},
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  /**
   * Puts the remote participant (the patient) of an ongoing call on hold, or resumes them.
   *
   * The browser Voice SDK has no hold() — unlike mute(), which is local and instant — so this is
   * necessarily a REST round-trip. The backend resolves which leg to hold: the frontend only knows
   * its own (parent) call SID, and holding the wrong leg would mute the nurse instead of the patient.
   *
   * @param parentCallSid The call SID this dialog holds: the agent's own leg on an outbound call,
   *                      the caller's leg on an inbound one. The backend resolves the patient leg.
   * @param hold true to hold, false to resume
   * @param participantCallSid Optional explicit leg (e.g. an added conference participant)
   * @returns Observable with the conference, the affected participant and the resulting hold state
   */
  setCallHold(parentCallSid: string, hold: boolean, participantCallSid?: string): Observable<HoldParticipantResult> {
    return this.http
      .post<ApiResponse<HoldParticipantResult>>(`${this.welbyCoreApiBaseUrl}/api/v1/twilio/${parentCallSid}/hold`, {
        hold,
        ...(participantCallSid && { participantCallSid }),
      })
      .pipe(map((response) => response.data));
  }

  updateRecordConsent(callSid: string, recordConsent: boolean): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.welbyCoreApiBaseUrl}/api/v1/twilio/${callSid}/record-consent`,
      { recordConsent }
    );
  }

  /**
   * Tells the backend which patient a call went to, so it can send them the NPS survey when the call
   * ends. This is the only way the patient id reaches the backend for a browser call: it is never sent
   * to Twilio (see `makeBrowserCall`).
   *
   * @param callSid The agent's own (parent) call SID
   * @param patientId The patient the call was placed to
   */
  registerPostCallSurvey(callSid: string, patientId: string): Observable<void> {
    return this.http
      .put<ApiResponse<void>>(`${this.welbyCoreApiBaseUrl}/api/v1/post-call-surveys/${callSid}`, { patientId })
      .pipe(
        // Only a failure that may be transient is retried: a 4xx (no access, not a patient, already
        // registered for someone else) will be refused the same way every time.
        retry({
          count: POST_CALL_SURVEY_REGISTER_RETRIES,
          delay: (error: HttpErrorResponse) =>
            error?.status >= 400 && error?.status < 500
              ? throwError(() => error)
              : rxTimer(POST_CALL_SURVEY_REGISTER_RETRY_DELAY_MS),
        }),
        map(() => undefined)
      );
  }

  /**
   * What the backend decided for a registered call's NPS survey. Only the care manager who registered
   * the call can read it.
   *
   * @param callSid The agent's own (parent) call SID
   */
  getPostCallSurveyOutcome(callSid: string): Observable<PostCallSurveyOutcome> {
    return this.http
      .get<ApiResponse<PostCallSurveyOutcome>>(`${this.welbyCoreApiBaseUrl}/api/v1/post-call-surveys/${callSid}`)
      .pipe(map((response) => response.data));
  }

  /**
   * Asks for a registered call's survey outcome until the backend settles it, and emits once: the settled
   * outcome, or — when the last check passes first — the last one seen, so the caller can tell a send
   * still in progress (`processing`) from a report that never arrived (`pending`, or null when no check
   * answered). A failed or hung check counts as no answer and does not stop the next one.
   *
   * @param callSid The agent's own (parent) call SID
   */
  waitForPostCallSurveyOutcome(callSid: string): Observable<PostCallSurveyOutcome | null> {
    return rxTimer(POST_CALL_SURVEY_OUTCOME_FIRST_CHECK_MS, POST_CALL_SURVEY_OUTCOME_INTERVAL_MS).pipe(
      take(POST_CALL_SURVEY_OUTCOME_CHECKS),
      concatMap(() =>
        this.getPostCallSurveyOutcome(callSid).pipe(
          timeout(POST_CALL_SURVEY_OUTCOME_REQUEST_TIMEOUT_MS),
          catchError(() => of(null))
        )
      ),
      scan((latest: PostCallSurveyOutcome | null, outcome: PostCallSurveyOutcome | null) => outcome ?? latest, null),
      takeWhile((outcome) => !outcome || !POST_CALL_SURVEY_SETTLED_STATUSES.includes(outcome.status), true),
      last(undefined, null)
    );
  }

  subscribeToParticipantCallLeg(sessionId: string, callSid: string): Observable<CallSession | undefined> {
    return this.firestoreService.doc<CallSession>(`call_sessions/${sessionId}`).valueChanges();
  }

  /**
   * Raises the required-microphone alert as soon as the microphone becomes unavailable, instead of
   * leaving it to be discovered when the care manager presses Call (or answers one).
   *
   * Only on a change: a care manager who chose "Not now" is not asked again until the microphone
   * has come back and gone away once more. The Call button stays disabled meanwhile.
   */
  private watchMicrophoneAvailability(): void {
    if (this.microphoneWatch) {
      return;
    }
    this.microphoneWatch = this.microphoneAvailability.availability$.subscribe((availability) => {
      if (!isMicrophoneUnavailable(availability)) {
        this.microphoneAlertPending = false;
        return;
      }
      if (this.device?.isBusy) {
        // Not over a live call: its text is about placing and answering calls, which reads as wrong
        // mid-conversation, and the in-call banner already covers a microphone that stops sending.
        // Raised when the call ends, if the problem is still there.
        this.microphoneAlertPending = true;
        return;
      }
      this.openMicrophoneRequiredDialog(availability);
    });
    this.microphoneAvailability.start();
  }

  /** Raises the alert held back during a call, once it has ended and only if still relevant. */
  private raisePendingMicrophoneAlert(): void {
    if (!this.microphoneAlertPending) {
      return;
    }
    this.microphoneAlertPending = false;
    const availability = this.microphoneAvailability.current;
    if (isMicrophoneUnavailable(availability)) {
      this.openMicrophoneRequiredDialog(availability);
    }
  }

  private async acquireAndVerifyMicrophone(context: { patientId?: string; fromLine?: string }): Promise<MicrophoneCheckResult> {
    const result = await this.checkMicrophone(context);
    // What opening the microphone found is the most reliable availability signal there is.
    this.microphoneAvailability.recordCheckResult(result.status);
    return result;
  }

  private async checkMicrophone(context: { patientId?: string; fromLine?: string }): Promise<MicrophoneCheckResult> {
    const audio = this.device?.audio;
    if (!audio) {
      // No Device means no call is possible either; makeBrowserCall reports that on its own.
      return { status: 'unsupported' };
    }

    // Read before anything is released, so a device the care manager chose is the one reopened.
    let deviceId: string | null;
    try {
      deviceId = await this.resolveInputDeviceId(audio);
    } catch (error) {
      // The priming getUserMedia failed. Its reason matters: a blocked permission used to be reported
      // as "no microphone found", sending the care manager to check a headset that was fine.
      const failure = this.describeAcquisitionFailure(error);
      if (failure.status === 'acquisition-failed') {
        // Busy or held elsewhere: says nothing about the microphone. Let the SDK try at connect.
        return { ...failure, status: 'unsupported' };
      }
      this.reportMicrophoneFailure(failure, context);
      return failure;
    }
    if (!deviceId) {
      this.reportMicrophoneFailure({ status: 'no-device' }, context);
      return { status: 'no-device' };
    }

    let result: MicrophoneCheckResult = { status: 'no-device' };

    for (let attempt = 0; attempt < 2; attempt++) {
      // Always start from a released device. `setInputDevice` returns early when the requested
      // device is already the selected one, which would hand this call the *previous* call's track —
      // precisely the stale track this method exists to keep out of a call.
      await audio.unsetInputDevice().catch(() => undefined);

      try {
        await audio.setInputDevice(deviceId);
      } catch (error) {
        result = this.describeAcquisitionFailure(error);
        // A refused permission is a decision, not a glitch: re-asking cannot change it.
        if (result.status === 'permission-denied') {
          break;
        }
        continue;
      }

      result = await this.microphoneCheck.verifyStream(audio.inputStream);
      this.lastMicrophoneReading = result;
      if (result.status === 'ok' || result.status === 'unsupported') {
        return result;
      }
      // Re-opening the same device cannot turn it into a microphone.
      if (result.status === 'loopback-device') {
        break;
      }
    }

    // Reported, not retried again, and handed back for the caller to warn about: a quiet input is
    // the one outcome the check is not certain about, so it must not cost the call.
    if (result.status === 'very-quiet') {
      this.reportMicrophoneFailure(result, context);
      return result;
    }

    this.reportMicrophoneFailure(result, context);

    // Every remaining verdict ends with the device in this method's hands and nothing downstream to
    // take it back: no call is placed on a blocking one, and an acquisition failure hands dialling
    // over to the SDK's own getUserMedia. Without releasing here the tab holds the microphone open —
    // and the operating system's recording indicator lit — until the care manager tries again, which
    // on a failed check can be a long time.
    await audio.unsetInputDevice().catch(() => undefined);

    if (result.status === 'acquisition-failed') {
      // The check could not obtain a device to look at — which says nothing about the microphone,
      // only about this attempt. Let the call proceed: the SDK will do its own getUserMedia at
      // connect, exactly as it did before this check existed. Refusing to dial here would trade an
      // intermittent "the patient cannot hear me" for a hard "I cannot call", which is strictly
      // worse. `unsupported` is the status both dialogs read as "dial anyway".
      return { ...result, status: 'unsupported' };
    }

    return result;
  }

  /** Labels the detector behind a warning, and carries its measured statistic where there is one. */
  private describeWarning(data?: CallWarningData | null): string {
    const detector = callWarningDetector(data);
    const measured = typeof data?.value === 'number' ? ` sd=${Math.round(data.value * 10) / 10}` : '';
    const limit = typeof data?.threshold?.value === 'number' ? ` limit=${data.threshold.value}` : '';
    return ` detector=${detector}${measured}${limit}`;
  }

  /**
   * Renders a reading as stable `key=value` pairs.
   *
   * Written to be parsed, not only read: these lines are the only record of what the browser
   * measured, and they are joined against energy measured on the Twilio-side recording to decide
   * whether the quiet threshold predicts a dead channel. A prose sentence would make that join a
   * guessing game.
   */
  private describeReading(reading: MicrophoneCheckResult | null): string {
    if (!reading) {
      return '';
    }
    const level = typeof reading.rmsDbfs === 'number' ? ` rms=${reading.rmsDbfs}dBFS peak=${reading.peakDbfs}dBFS` : '';
    const device = reading.label ? ` device="${reading.label}"` : '';
    return ` mic=${reading.status}${level}${device}`;
  }

  /** Leaves a durable trace of a microphone that never reached the call, off the browser. */
  private reportMicrophoneFailure(result: MicrophoneCheckResult, context: { patientId?: string; fromLine?: string }): void {
    console.error('[Twilio] Microphone check failed', result);
    this.reportClientCallError({
      message: `Microphone check failed:${this.describeReading(result)}`,
      source: 'connect',
      patientId: context.patientId,
      fromLine: context.fromLine,
    });
  }

  /**
   * Turns a failed acquisition into a status.
   *
   * The names come from the browser, not from Twilio: `setInputDevice` surfaces the raw
   * `getUserMedia` rejection, and the only distinction the care manager can act on is
   * "you denied the microphone" versus "the device did not open".
   */
  private describeAcquisitionFailure(error: unknown): MicrophoneCheckResult {
    const errorName = (error as { name?: string })?.name;

    if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError' || errorName === 'SecurityError') {
      return { status: 'permission-denied', errorName };
    }
    // There is no input to open at all, so dialling would only produce the silence this prevents.
    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError' || errorName === 'OverconstrainedError') {
      return { status: 'no-device', errorName };
    }
    // Everything else — the device busy, held by another application, or an acquisition that lost a
    // race — says nothing about whether the microphone works. Kept separate so it never blocks a call.
    return { status: 'acquisition-failed', errorName };
  }

  /**
   * Runs audio-device work one operation at a time.
   *
   * The installed SDK (2.11.1) does not serialise `setInputDevice`/`unsetInputDevice`: its AsyncQueue
   * is used only by the sound cache, so two overlapping acquisitions race, and a `connect()` that
   * starts while one is in flight fails with AcquisitionFailedError (31402, call.ts:726) — the class
   * of bug Twilio fixed in 2.11.2 and 2.12.1, neither of which this app has. The realistic overlap is
   * a care manager hanging up and immediately redialling: the release of the old device would still
   * be running when the next call acquires. Queueing here makes that ordering explicit rather than
   * dependent on the SDK version, and the guarantee survives the eventual upgrade.
   */
  private runExclusively<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.audioOperations.then(operation, operation);
    // The chain must not inherit a rejection, or every later operation would be skipped.
    this.audioOperations = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }

  /**
   * Picks the input device to open: whatever is already selected, otherwise the system default.
   *
   * The SDK only knows the devices it has enumerated, and a browser publishes nothing until some
   * capture has been granted — so on the very first call of a session the list can be empty and a
   * priming `getUserMedia` is what populates it. Its stream is discarded immediately; the call uses
   * the one `setInputDevice` opens.
   *
   * Before access is granted the browser lists each input with an empty `deviceId`, and the SDK keys
   * its list by that id — so an empty key is "a device exists, id unknown", never something to open.
   * The SDK only re-reads the list on `devicechange`, which Chrome does not always fire when access
   * is granted (notably when it is allowed afterwards from the address bar). Without the refresh
   * below the list stayed on the empty id and every call was refused as "no microphone".
   */
  private async resolveInputDeviceId(audio: NonNullable<Device['audio']>): Promise<string | null> {
    const pick = (): string | null => {
      if (audio.inputDevice?.deviceId) {
        return audio.inputDevice.deviceId;
      }
      if (audio.availableInputDevices.has('default')) {
        return 'default';
      }
      return Array.from(audio.availableInputDevices.keys()).find((deviceId) => !!deviceId) ?? null;
    };

    const selected = pick();
    if (selected) {
      return selected;
    }

    // A rejection propagates: the caller turns its DOMException name into the right status.
    const priming = await navigator.mediaDevices.getUserMedia({ audio: true });
    priming.getTracks().forEach((track) => track.stop());

    return pick() ?? (await this.refreshSdkInputDevices(pick));
  }

  /**
   * Makes the SDK re-read the device list, through the same `devicechange` event it listens to, and
   * waits briefly for a usable id. The SDK's refresh is asynchronous and exposes nothing to await.
   */
  private async refreshSdkInputDevices(pick: () => string | null): Promise<string | null> {
    navigator.mediaDevices.dispatchEvent(new Event('devicechange'));
    for (let attempt = 0; attempt < SDK_DEVICE_REFRESH_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, SDK_DEVICE_REFRESH_INTERVAL_MS));
      const deviceId = pick();
      if (deviceId) {
        return deviceId;
      }
    }
    return null;
  }

  /**
   * Forwards the SDK's in-call quality warnings to the backend and to whichever dialog owns the call.
   *
   * `constant-audio-input-level` is the reason this exists: it is the SDK telling us the care
   * manager's input level has not moved for ten samples, i.e. the patient is listening to silence.
   * Nothing consumed it before, which is why the failures were only ever visible afterwards in the
   * recordings. The warning arrives about ten seconds in, so it is a safety net behind the pre-call
   * check, not a replacement for it.
   */
  private monitorCallAudio(call: Call, context: { patientId?: string; fromLine?: string } = {}): void {
    // Captured at attach time: by the time a warning fires, a later call could have replaced it.
    const reading = this.lastMicrophoneReading;

    // Each warning is reported once per call. A flapping network raises and clears the same one many
    // times (`high-packet-loss` fires on a single sample), and every report spends the care manager's
    // 10/min budget on /call-attempts/client-error — the row is evidence that it happened, not a count.
    // Keyed with the detector too, so a `stddev` constant-audio-input-level cannot hide a `flatline`.
    const reported = new Set<string>();

    const emit = (name: string, cleared: boolean, data?: CallWarningData | null) => {
      this.audioWarnings$.next({ name, cleared, call, data, callSid: call.parameters?.CallSid });

      const reportKey = `${name}:${callWarningDetector(data)}`;
      if (!cleared && REPORTED_CALL_WARNINGS.includes(name) && !reported.has(reportKey)) {
        reported.add(reportKey);
        this.reportClientCallError({
          // Two things ride along. The pre-call reading, because a warning on a call the check passed
          // is the false negative that says the quiet threshold is too low — only recognisable as one
          // when both numbers sit on the same row. And which detector fired, because
          // `constant-audio-input-level` means "nothing captured" from one of them and only "very
          // steady" from the other, and a row that cannot tell them apart invites reading a false
          // positive as proof.
          message: `Call warning: ${name}${this.describeWarning(data)}${this.describeReading(reading)}`,
          source: 'call',
          callSid: call.parameters?.CallSid,
          patientId: context.patientId,
          fromLine: context.fromLine,
        });
      }
    };

    call.on('warning', (name: string, data?: CallWarningData | null) => emit(name, false, data));
    call.on('warning-cleared', (name: string) => emit(name, true));

    // The check runs before the call exists, so its reading has no CallSid to be joined on. Once the
    // call is up it does, and for an outbound leg it is the PARENT SID — the same one the recordings
    // hang off, so a browser-side reading and the energy measured on Twilio's own recording can be
    // compared directly, with no intermediate table.
    const flagged = !!reading && isReportableMicrophoneFailure(reading.status);
    let acceptedAt: number | null = null;

    call.once('accept', () => {
      acceptedAt = Date.now();
      if (flagged) {
        this.reportClientCallError({
          message: `Microphone reading at connect${this.describeReading(reading)}`,
          source: 'call',
          callSid: call.parameters?.CallSid,
          patientId: context.patientId,
          fromLine: context.fromLine,
        });
      }
    });

    // The cell neither side can see otherwise: a call the check passed, that raised no warning, and
    // that the care manager abandoned within seconds — the shape of "the patient could not hear me".
    // Reporting every call would be ~660 rows a day; restricting it to short ones is ~64, and it is
    // the only population where a browser reading can be paired with a silent recording to learn how
    // far the quiet threshold is off. Duration is measured on this leg alone: the browser knows how
    // long its own call lasted and needs nothing from the PSTN side.
    // Its own listener: the one below returns early on most calls.
    call.once('disconnect', () => this.raisePendingMicrophoneAlert());

    call.once('disconnect', () => {
      if (!reading || flagged || acceptedAt === null) {
        return;
      }
      const seconds = Math.round((Date.now() - acceptedAt) / 1000);
      if (seconds > SHORT_CALL_SECONDS) {
        return;
      }
      this.reportClientCallError({
        message: `Microphone reading on short call duration=${seconds}s${this.describeReading(reading)}`,
        source: 'call',
        callSid: call.parameters?.CallSid,
        patientId: context.patientId,
        fromLine: context.fromLine,
      });
    });
  }

  /**
   * The browser state an error report carries: how old the Twilio token was, whether the tab was
   * hidden and for how long, and what the Device thought of itself. Built at report time, not when
   * the error was raised, which for these synchronous handlers is the same moment.
   */
  private describeClientContext(): CallClientContext {
    const nowSec = Date.now() / 1000;
    const { iat, exp } = this.twilioTokenClaims ?? {};
    return {
      ...(typeof iat === 'number' && { tokenAgeSec: Math.round(nowSec - iat) }),
      ...(typeof exp === 'number' && { tokenExpiresInSec: Math.round(exp - nowSec) }),
      documentHidden: document.visibilityState === 'hidden',
      msSinceVisibilityChange: Math.max(0, Date.now() - this.visibilityChangedAt),
      ...(this.device?.state && { deviceState: this.device.state }),
    };
  }

  /** A token that cannot be decoded leaves the claims empty: the report then omits the token fields. */
  private rememberTokenClaims(token: string): void {
    try {
      const { iat, exp } = jwt_decode<{ iat?: number; exp?: number }>(token);
      this.twilioTokenClaims = { iat, exp };
    } catch {
      this.twilioTokenClaims = null;
    }
  }

  /** The Voice SDK types its errors without a `code`, but every TwilioError carries one at runtime. */
  private sdkErrorCode(error: unknown): number | undefined {
    return (error as { code?: number })?.code;
  }

  /**
   * The SDK's `message` is free text built from Twilio's description + explanation + causes, and for
   * invalid-destination errors it can embed the dialed number. It is persisted and later rendered
   * into the daily XLSX, so redact anything phone-shaped and cap the length here — the backend DTO
   * REJECTS over 500 chars, which would silently drop the whole report instead of trimming it.
   */
  private sanitizeClientError(payload: CallClientError): CallClientError {
    const redacted = (payload.message ?? '').replace(/\+?\d[\d\-().\s]{9,}\d/g, '[redacted]').slice(0, 500);
    return { ...payload, message: redacted };
  }

  private showIncomingCallDialog(call: Call): void {
    const callerNumber = call.parameters.From;
    const callerName = call.customParameters.get('callerName') || null;
    // Both read off the one `callType` parameter markus stamps on the leg, and both known
    // synchronously — the dialog opens already labelled instead of resolving anything while the
    // phone is ringing. A leg carries at most one of these; an unlabelled leg carries neither.
    const legCallType = call.customParameters.get('callType');
    const isPostDischargeCall = legCallType === DISCHARGE_CALL_TYPE;
    const isWarmTransfer = legCallType === WARM_TRANSFER_CALL_TYPE;
    // Only a warm transfer carries it, and only when markus could resolve the clinic.
    const clinicName = call.customParameters.get('clinicName') || null;
    const availability = this.microphoneAvailability.current;
    const microphoneUnavailable = isMicrophoneUnavailable(availability) ? availability : undefined;
    let dialogRef: MatDialogRef<IncomingCallDialogComponent> | null = null;

    const cleanup = () => {
      if (dialogRef) {
        dialogRef.close({ action: 'ended' });
        dialogRef = null;
      }
      call.off('disconnect', onDisconnect);
      call.off('cancel', onDisconnect);
      call.off('reject', onDisconnect);
    };

    const onDisconnect = () => {
      cleanup();
      this.afterCallEnded();
    };

    // Set up event listeners for call ending
    call.on('disconnect', onDisconnect);
    call.on('cancel', onDisconnect);
    call.on('reject', onDisconnect);

    dialogRef = this.dialog.open(IncomingCallDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        call,
        callerNumber,
        callerName,
        isPostDischargeCall,
        isWarmTransfer,
        clinicName,
        microphoneUnavailable,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      cleanup();

      if (!result) {
        call.ignore();
        this.afterCallEnded();
        return;
      }

      switch (result.action) {
        case 'accept':
          // Unlike an outbound call, a failed check must not cancel the answer: dropping a clinical
          // transfer is worse than answering with a microphone that may be dead. The check still
          // runs first because re-acquiring the device is what usually repairs it, and whatever it
          // finds is carried into the in-call UI so the care manager is told rather than left
          // talking into silence.
          this.acceptIncomingCall(call, callerNumber, callerName);
          break;
        case 'reject':
          call.reject();
          this.afterCallEnded();
          break;
        default:
          call.ignore();
          this.afterCallEnded();
      }
    });
  }

  private async acceptIncomingCall(call: Call, callerNumber: string, callerName?: string): Promise<void> {
    // Before the microphone check: the caller may hang up while it runs, and the incoming dialog's own
    // listeners are already gone. This is what tells a tab that was refused the Device during the call
    // that it may take it now.
    call.on('disconnect', () => this.afterCallEnded());
    call.on('cancel', () => this.afterCallEnded());
    const microphone = await this.ensureMicrophoneReady();
    // Attached before accepting: the 'accept' event is what carries the CallSid onto the reading, and
    // a listener added after the call is already up can miss it.
    this.monitorCallAudio(call);
    // The Device does not forward call errors in SDK 2.11, so without this an inbound call that fails
    // leaves no trace. Not sent to call-attempts/client-error: that report is outbound-only.
    call.on('error', (error) => {
      console.error('[Twilio] Inbound call error', error);
      this.errorReporter.report('twilio-call', error, {
        code: this.sdkErrorCode(error),
        callSid: call.parameters?.CallSid,
        prefix: 'Inbound call error',
      });
    });
    call.accept();
    this.showCallInProgressDialog(call, callerNumber, callerName, microphone);
  }

  private showCallInProgressDialog(
    call: Call,
    callerNumber: string,
    callerName?: string,
    microphone?: MicrophoneCheckResult
  ): void {
    const progressDialogRef = this.dialog.open(CallInProgressDialogComponent, {
      width: '450px',
      height: 'auto',
      disableClose: true,
      // Keep the floating call UI alive across route changes. Without this, Angular Material's
      // default (closeOnNavigation: true) tears down the dialog when the user navigates to
      // another chart while the Twilio call is still connected, leaving an active call with no
      // controls. The dialog still closes normally when the call ends (onCallEnded).
      closeOnNavigation: false,
      hasBackdrop: false,
      panelClass: 'call-in-progress-draggable',
      backdropClass: 'call-in-progress-backdrop',
      position: {
        top: '0px',
        left: '0px',
      },
      data: {
        call,
        callerNumber,
        callerName,
        microphone,
      },
    });
  }

  /** Creates and registers this tab's Device, with a token that is fresh enough to call on. */
  private activateDevice(): Promise<void> {
    if (!this.activation) {
      const activation = this.createDevice().finally(() => {
        // Sign-out may have dropped it already, and a new session may have started its own.
        if (this.activation === activation) {
          this.activation = null;
        }
      });
      this.activation = activation;
    }
    return this.activation;
  }

  /** Never rejects: a failure is reported and retried, so callers only check whether a Device exists. */
  private async createDevice(): Promise<void> {
    const identity = this.twilioIdentity;
    if (!identity) {
      return;
    }
    // A tab that takes the Device back shortly after losing it reuses its token instead of fetching
    // a new one on every tab switch.
    let token = this.startupToken ?? this.lastToken;
    this.startupToken = null;
    if (!token || this.secondsLeftOn(token) < environment.twilioDevice.minTokenLifetimeSec) {
      try {
        token = await this.fetchTokenWithoutSignOut(identity);
      } catch (error) {
        if (identity === this.twilioIdentity) {
          this.reportTokenFailure(error, 'Twilio token fetch failed');
          this.scheduleActivationRetry();
        }
        return;
      }
    }
    // The tab may have signed out (or another user signed in), or lost the Device to another tab,
    // while the token was fetched. A token fetched for another identity is never used.
    if (identity !== this.twilioIdentity || !this.isActiveCallingTab || this.device) {
      return;
    }
    try {
      this.rememberTokenClaims(token);
      this.lastToken = token;
      console.log('[Twilio] This tab now holds the calling Device');
      this.device = this.buildDevice(token);
      this.addDeviceListeners(this.device);
      this.scheduleTokenRefresh();
      this.device.register().catch(() => undefined); // failures surface through the 'error' listener
    } catch (error) {
      // e.g. the browser is not supported, or the token was rejected as malformed.
      this.device?.destroy();
      this.device = null;
      this.lastToken = null;
      this.twilioTokenClaims = null;
      this.reportTokenFailure(error, 'Twilio Device could not be created');
      // A browser Twilio does not support will never get a Device; retrying would only repeat the report.
      if (Device.isSupported) {
        this.scheduleActivationRetry();
      }
    }
  }

  private buildDevice(token: string): Device {
    return new Device(token, {
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      tokenRefreshMs: environment.twilioDevice.tokenRefreshMs,
    });
  }

  /** Destroys this tab's Device: another tab took it, or the care manager signed out. */
  private releaseDevice(): void {
    if (this.tokenRefreshRetry) {
      clearTimeout(this.tokenRefreshRetry);
      this.tokenRefreshRetry = null;
    }
    if (this.scheduledTokenRefresh) {
      clearTimeout(this.scheduledTokenRefresh);
      this.scheduledTokenRefresh = null;
    }
    if (this.activationRetry) {
      clearTimeout(this.activationRetry);
      this.activationRetry = null;
    }
    if (this.device) {
      console.log('[Twilio] This tab released the calling Device');
      // Best effort: the SDK sends the unregister over the signaling stream, which destroy() tears
      // down right after, so Twilio may still only drop this client when it notices the socket close.
      if (this.device.state === Device.State.Registered) {
        this.device.unregister().catch(() => undefined);
      }
      this.device.destroy();
      this.device = null;
    }
    this.twilioTokenClaims = null;
  }

  /**
   * The care manager is looking at this tab: incoming calls should ring here, so it takes the Device
   * once the tab has stayed in front for a moment (flicking through tabs does not move it).
   */
  private onTabShown(): void {
    if (!this.twilioIdentity) {
      return;
    }
    this.repairDevice();
    if (!this.coordinator.isSupported || this.coordinator.isHolder) {
      return;
    }
    this.cancelVisibleTakeover();
    this.visibleTakeover = setTimeout(async () => {
      this.visibleTakeover = null;
      // Became the holder meanwhile (e.g. the holder tab was closed): nothing to take.
      if (this.coordinator.isHolder) {
        return;
      }
      // The care manager did not ask for this move, and the holder has a working Device: it is only
      // taken once this tab knows it can build its own.
      if (!(await this.ensureTakeoverToken())) {
        return;
      }
      if (document.visibilityState === 'visible' && this.twilioIdentity && (await this.coordinator.takeOver())) {
        await this.repairDevice();
      }
    }, environment.twilioDevice.visibleTakeoverDelayMs);
  }

  /**
   * Makes sure the token createDevice() will use after a takeover is fresh, fetching one if needed.
   * Resolves `false` when it cannot, so the Device stays with the tab that has a working one.
   */
  private async ensureTakeoverToken(): Promise<boolean> {
    const identity = this.twilioIdentity;
    if (!identity || document.visibilityState !== 'visible') {
      return false;
    }
    const cached = this.startupToken ?? this.lastToken;
    if (cached && this.secondsLeftOn(cached) >= environment.twilioDevice.minTokenLifetimeSec) {
      return true;
    }
    try {
      const token = await this.fetchTokenWithoutSignOut(identity);
      if (identity !== this.twilioIdentity) {
        return false;
      }
      this.startupToken = null;
      this.lastToken = token;
      return true;
    } catch (error) {
      // An expired Welby session fails on every tab switch until sign-in; not worth a report each time.
      if (identity === this.twilioIdentity && !this.tokenService.tokenIsExpired()) {
        this.reportTokenFailure(error, 'Twilio token fetch before takeover failed');
      }
      return false;
    }
  }

  private cancelVisibleTakeover(): void {
    if (this.visibleTakeover) {
      clearTimeout(this.visibleTakeover);
      this.visibleTakeover = null;
    }
  }

  /** On, ringing with, or about to place a call: the Device must not move to another tab. */
  private isBusy(): boolean {
    return !!this.callPending || !!this.device?.isBusy || (this.device?.calls?.length ?? 0) > 0;
  }

  private markCallPending(): void {
    this.clearCallPending();
    this.callPending = setTimeout(() => {
      this.callPending = null;
      this.afterCallEnded();
    }, environment.twilioDevice.callPendingTimeoutMs);
  }

  private clearCallPending(): void {
    if (this.callPending) {
      clearTimeout(this.callPending);
      this.callPending = null;
    }
  }

  /**
   * Lets a tab that was refused the Device during this call take it now. Checked on the next tick:
   * inside the SDK's own 'disconnect' event the call may still be counted as active.
   */
  private afterCallEnded(): void {
    setTimeout(() => {
      if (!this.isBusy()) {
        this.coordinator.notifyIdle();
      }
    });
  }

  /** A holder whose Device could not be created tries again, so incoming calls do not ring nowhere. */
  private scheduleActivationRetry(): void {
    // An expired Welby session cannot get a token until the care manager signs in again; retrying
    // would only report the same failure every minute.
    if (this.activationRetry || !this.isActiveCallingTab || this.tokenService.tokenIsExpired()) {
      return;
    }
    this.activationRetry = setTimeout(() => {
      this.activationRetry = null;
      if (this.twilioIdentity && this.isActiveCallingTab && !this.device) {
        this.activateDevice();
      }
    }, environment.twilioDevice.tokenRefreshRetryMs);
  }

  /** Refreshes a stale token and re-registers a dropped Device, if this tab holds one. */
  private async repairDevice(): Promise<void> {
    if (this.device && this.isActiveCallingTab) {
      await this.ensureRegistered();
    }
  }

  /** Resolves `true` once the Device is registered, refreshing a stale token first. */
  private async ensureRegistered(): Promise<boolean> {
    const device = this.device;
    if (!device || device.state === Device.State.Destroyed) {
      return false;
    }
    const exp = this.twilioTokenClaims?.exp;
    if (typeof exp !== 'number' || exp - Date.now() / 1000 < environment.twilioDevice.minTokenLifetimeSec) {
      await this.refreshToken();
    }
    if (device.state === Device.State.Registered) {
      return true;
    }
    if (device.state === Device.State.Unregistered) {
      device.register().catch(() => undefined);
    }
    return this.waitForRegistration(device);
  }

  private waitForRegistration(device: Device): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const onRegistered = () => finish(true);
      const timer = setTimeout(() => finish(device.state === Device.State.Registered), environment.twilioDevice.registrationTimeoutMs);
      const finish = (registered: boolean) => {
        clearTimeout(timer);
        device.off('registered', onRegistered);
        resolve(registered);
      };
      device.on('registered', onRegistered);
    });
  }

  /**
   * Gets a new token and hands it to the Device. Concurrent callers share one request. A failure is
   * reported and retried later; it never signs the care manager out (see fetchTokenWithoutSignOut).
   */
  private refreshToken(): Promise<boolean> {
    if (!this.tokenRefresh) {
      const refresh = this.doRefreshToken().finally(() => {
        if (this.tokenRefresh === refresh) {
          this.tokenRefresh = null;
        }
      });
      this.tokenRefresh = refresh;
    }
    return this.tokenRefresh;
  }

  private async doRefreshToken(): Promise<boolean> {
    // Captured before the request: if the Device was released, or the care manager signed out and
    // someone else signed in, while it was in flight, the token belongs to a Device that is gone and
    // must not be handed to the one that replaced it (it would register that tab as the old user).
    const identity = this.twilioIdentity;
    const device = this.device;
    if (!identity || !device) {
      return false;
    }
    const stillCurrent = () => identity === this.twilioIdentity && device === this.device;
    try {
      const newToken = await this.fetchTokenWithoutSignOut(identity);
      if (!stillCurrent()) {
        return false;
      }
      device.updateToken(newToken);
      this.rememberTokenClaims(newToken);
      this.lastToken = newToken;
      this.scheduleTokenRefresh();
      return true;
    } catch (error) {
      if (!stillCurrent()) {
        return false;
      }
      console.error('Error refreshing Twilio token:', error);
      this.reportTokenFailure(error, 'Twilio token refresh failed');
      if (!this.tokenRefreshRetry) {
        this.tokenRefreshRetry = setTimeout(() => {
          this.tokenRefreshRetry = null;
          this.refreshToken();
        }, environment.twilioDevice.tokenRefreshRetryMs);
      }
      return false;
    }
  }

  /**
   * Fetches a Twilio token without going through AuthInterceptor. That interceptor turns any 401 on
   * the API into a sign-out, and a background tab refreshing its token with a stale Welby session
   * would otherwise sign the care manager out of every tab. The session token is attached by hand.
   */
  private async fetchTokenWithoutSignOut(identity: string): Promise<string> {
    if (this.tokenService.tokenIsExpired()) {
      throw new Error('Welby session token is expired');
    }
    const response = await this.telemetryHttp
      .get<{ token: string }>(`${this.welbyCoreApiBaseUrl}/api/v1/twilio/token`, {
        params: { identity },
        headers: { Authorization: `Bearer ${this.tokenService.getToken()}` },
      })
      .pipe(timeout(environment.twilioDevice.tokenFetchTimeoutMs))
      .toPromise();
    if (typeof response?.token !== 'string' || !response.token) {
      throw new Error('Twilio token response did not contain a token');
    }
    return response.token;
  }

  /**
   * Refreshes the token `tokenRefreshMs` before its own `exp`. The SDK's tokenWillExpire is scheduled
   * from the TTL Twilio reports at registration, which for a token this tab reused may not reflect the
   * time it actually has left; both paths share one request (see refreshToken).
   */
  private scheduleTokenRefresh(): void {
    if (this.scheduledTokenRefresh) {
      clearTimeout(this.scheduledTokenRefresh);
      this.scheduledTokenRefresh = null;
    }
    const { iat, exp } = this.twilioTokenClaims ?? {};
    if (typeof exp !== 'number' || !this.device) {
      return;
    }
    const msLeft = exp * 1000 - Date.now();
    const lifeMs = typeof iat === 'number' ? (exp - iat) * 1000 : null;
    // The local clock can be wrong (set by hand, never synced). A token that already looks expired, or
    // has more time left than it was ever issued for, is timed by its own lifetime instead, so a wrong
    // clock cannot turn this into a back-to-back refresh loop.
    const remainingMs = lifeMs !== null && (msLeft <= 0 || msLeft > lifeMs) ? lifeMs : msLeft;
    // Half the remaining life at least, so a token shorter-lived than tokenRefreshMs does not loop
    // either; and never sooner than the retry delay.
    const delayMs = Math.max(remainingMs - environment.twilioDevice.tokenRefreshMs, remainingMs / 2, environment.twilioDevice.tokenRefreshRetryMs);
    this.scheduledTokenRefresh = setTimeout(() => {
      this.scheduledTokenRefresh = null;
      this.refreshToken();
    }, delayMs);
  }

  /**
   * A token that could not be fetched leaves calling broken with no SDK error, so it is reported, to
   * the client error reporter rather than as a call attempt: no call was attempted.
   */
  private reportTokenFailure(error: unknown, message: string): void {
    // Each retry fails the same way; the reporter folds the repeats into one event.
    this.errorReporter.report('twilio-device', error, { prefix: message });
  }

  /** Seconds the token has left, or 0 when it cannot be decoded. */
  private secondsLeftOn(token: string): number {
    try {
      const { exp } = jwt_decode<{ exp?: number }>(token);
      return typeof exp === 'number' ? exp - Date.now() / 1000 : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Posts to markus-call-processor `text-message/send` with the signed-in user's Firebase ID token.
   *
   * markus requires a caller credential on this route (a service key or a Firebase ID token, plus
   * access to the patient when one is named). `AuthInterceptor` does not touch markus URLs, so the
   * header is added here, the same way `PatientLookupService` does it.
   */
  private postTextMessage(body: object): Promise<SendTextResponse> {
    return this.firebaseIdToken
      .authHeaders()
      .pipe(
        switchMap((headers) =>
          this.http.post<SendTextResponse>(`${this.welbyCommunicationEndpoint}/text-message/send`, body, { headers })
        ),
        take(1)
      )
      .toPromise();
  }
}
